// Note that this script is currently loaded via the navbar and not via this block because the viewScript of this block.json is not working.

import { t } from '../../../assets/utils/algolia/algoliaLocalization'
import { store } from '@wordpress/interactivity'

type TranslatedValue<T> = Record<string, T> | T

type Location = {
  slug: TranslatedValue<string>
  name: TranslatedValue<string>
  status: string
  _rankingInfo: {
    geoDistance: number
  }
}

type TranslatedLocation = Location & {
  name: string
  slug: string
  redirectUrl: string
  distance: number
}

type State = {
  baseUrl?: string // this will be set by the wp_interactivity_state
  microchurchSlug?: string // this will be set by the wp_interactivity_state
  isActive: boolean
  isDataFetching: boolean
  isDataPending: boolean
  isDataError: boolean
  lastFetch: number
  data: TranslatedLocation[]
}

function getLocationRedirectUrl(location: Location) {
  const slug = t(location.slug ?? '', { fallback: 'any' })
  if (location.status === 'microchurch' && state.microchurchSlug) {
    return `${state.baseUrl}/${state.microchurchSlug}/${slug}`
  }
  return `${state.baseUrl}/${slug}`
}

function getLocationDistance(location: Location) {
  return Math.round(location._rankingInfo.geoDistance / 1000) ?? 0
}

const apiEndpoint = 'yt/v1/location-switcher/locations'

const initialState: State = {
  isActive: false,
  isDataFetching: false,
  isDataPending: true,
  isDataError: false,
  lastFetch: 0,
  data: []
}

const { state, actions } = store('icf-location-switcher', {
  state: {
    ...initialState,
    get hasData(): boolean {
      return state.data?.length > 0
    },
    get showSpinner(): boolean {
      return state.isDataPending && !state.isDataError
    }
  },
  actions: {
    reset() {
      Object.assign(state, initialState)
    },
    setActive(isActive: boolean) {
      state.isActive = isActive
      if (!isActive && state.isDataError) {
        actions.reset()
      }
    },
    fetch() {
      if (!state.isActive || state.isDataFetching || !state.isDataPending) {
        return
      }

      if (state.isDataError && Date.now() < state.lastFetch + 1000 * 5) {
        return
      }

      state.lastFetch = Date.now()

      state.isDataFetching = true

      fetch(`${window.YT_CONFIG.restUrl}${apiEndpoint}`, {
        headers: { 'Cache-Control': 'no-cache' }
      })
        .then((data) => data.json())
        .then((data) => {
          if (!Array.isArray(data)) {
            throw new Error('Locations are not an array')
          }
          state.data = (data as Location[]).map((location: Location) => ({
            ...location,
            slug: t(location.slug ?? '', { fallback: 'any' }),
            name: t(location.name ?? '', { fallback: 'any' }),
            redirectUrl: getLocationRedirectUrl(location),
            distance: getLocationDistance(location)
          }))
          state.isDataPending = false
          state.isDataError = false
        })
        .catch((error) => {
          console.error(
            'Location Switcher: Error when retrieving locations:',
            error
          )
          state.isDataError = true
        })
        .finally(() => {
          state.isDataFetching = false
        })
    }
  }
})

const dropdowns = Array.from(
  document.querySelectorAll<HTMLElement>('.location-switcher-dropdown')
)

function isSomeDropdownOpen() {
  return dropdowns.some((dropdown) => dropdown.classList.contains('show'))
}

for (const dropdown of dropdowns) {
  const trigger = dropdown.querySelector<HTMLElement>('button')
  if (trigger) {
    trigger.addEventListener('show.bs.dropdown', () => {
      actions.setActive(true)
    })
    trigger.addEventListener('hide.bs.dropdown', () => {
      if (!isSomeDropdownOpen()) {
        actions.setActive(false)
      }
    })
  }
}
